/**
 * How a template starts (Feature 77 D13): when its first capability
 * completes, when it is called (Feature 78), or on a schedule, which runs
 * the first capability with a fixed input at each fire (Feature 80 D4,
 * D5). `first` is that capability on every start; on a call it is the
 * request, its input and no result yet.
 */
import type { LinkageStart } from '@optum/api-client/types';

import { cronReading } from '../../lib/schedule.js';

/** The start of every template saved before Feature 77, and of a new one. */
export const COMPLETION: LinkageStart = { kind: 'completion', cron: null };

/** A call's start: `first` is the request as it is accepted (Feature 78 D1). */
export const CALL: LinkageStart = { kind: 'call', cron: null };

/** Whether a start is a call (Feature 78). */
export function isCall(start: LinkageStart | undefined): boolean {
  return start?.kind === 'call';
}

/** Whether a start is a schedule. */
export function isSchedule(start: LinkageStart | undefined): boolean {
  return start?.kind === 'schedule';
}

/**
 * A start in words: `when <capability> completes`, `when <capability> is
 * called`, or `<reading> · runs <capability>` with its UTC cron (Feature 80).
 */
export function startWords(
  start: LinkageStart | undefined,
  firstCapabilityId: string | null,
  timeZone: string,
): { words: string; cron: string | null } {
  if (isCall(start)) return { words: `when ${firstCapabilityId ?? '—'} is called`, cron: null };
  if (!isSchedule(start))
    return { words: `when ${firstCapabilityId ?? '—'} completes`, cron: null };
  const cron = start?.cron ?? '';
  const when = cronReading(cron, timeZone) ?? 'On a schedule';
  return { words: `${when} · runs ${firstCapabilityId ?? '—'}`, cron: `${cron} UTC` };
}
